/**
 * WX System User — pass-through identity for internal admin sessions.
 *
 * The identity has two spellings, and both reach this app.
 *
 * WX issues JWTs with `user.email = system@fountain.com` — the bare
 * sentinel, the same string for every account. Hire cannot store it that
 * way, because one row per account has to be addressable, so it keeps the
 * user as `system+<account.wx_external_id>@fountain.com`
 * (`User.email_from_wx_jwt` / `User#wx_system_email?` in apps/hire) and
 * rewrites the sentinel on every JWT lookup. Monolith `whoami` therefore
 * answers with the plus-addressed form, which is the email
 * `getUserDataForSystemUser` puts on `GoCurrentUser` — so a session
 * established from the sentinel is read back under the per-account
 * address. `isSystemUserEmail` recognizes both, because a caller holding
 * only one of them cannot tell which spelling it has.
 *
 * The JWT also carries:
 *   - `user.impersonatedBy` — the actor admin's WX UUID (audit only)
 *   - `user.hireAccountId`  — the Account.monolithAccountId the session is
 *                              pinned to
 *
 * Hire Go has no Go DB row for this email; the session is resolved by
 * looking up the Account from `hireAccountId` and rendering the app inside
 * that account's scope. See lib/auth/establish-wx-session.ts for the
 * session-establishment branch.
 */
export const SYSTEM_USER_EMAIL = 'system@fountain.com';

/**
 * Hire's per-account storage form of the sentinel. Mirrors
 * `User#wx_system_email?` in apps/hire; the local part is pinned to a UUID
 * so an ordinary plus-address (`system+notes@fountain.com`) is not granted
 * the system-user branches.
 */
const SYSTEM_USER_PER_ACCOUNT_EMAIL =
  /^system\+[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}@fountain\.com$/;

export function isSystemUserEmail(email: string | null | undefined): boolean {
  if (!email) {
    return false;
  }
  const normalized = email.toLowerCase();
  return (
    normalized === SYSTEM_USER_EMAIL ||
    SYSTEM_USER_PER_ACCOUNT_EMAIL.test(normalized)
  );
}

/**
 * Look up an Account by `monolithAccountId`.
 *
 * Uses `findFirst` rather than `findUnique` because the unique constraint
 * on `accounts.monolith_account_id` was dropped in migration
 * `20251229215433_drop_uniqueness_on_accounts_monolith_account_id`
 * (AXA-1496) to support sandbox accounts that shared the same monolith id.
 * Sandbox accounts have since been migrated to a separate DB, so re-adding
 * `@unique` is safe and tracked separately — when that lands, switch this
 * helper to `findUnique`.
 */
export async function findAccountByMonolithAccountId(
  monolithAccountId: string
) {
  // The email predicate also runs in client-side permission helpers.
  const { db } = await import('@/lib/db');
  return db.account.findFirst({ where: { monolithAccountId } });
}
